import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import App from './App.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { ShopProvider } from './contexts/ShopContext.jsx';
import './index.css';
import {
  isAppControlledByUnexpectedWorker,
  clearAppBrowsingData,
  prepareServiceWorkerEnvironment,
} from './utils/serviceWorkerLifecycle';

// Auto-apply new service workers so production web users move to the latest
// deploy without waiting for a manual prompt interaction.
window.__pwaUpdateAvailable = false;
window.__pwaDoUpdate = null;
window.__pwaUpdateDetectedAt = 0;
let hasReloadedForServiceWorker = false;
let pwaRecoveryAttempted = false;
async function bootServiceWorkerRegistration() {
  if (import.meta.env.VITE_DISABLE_PWA === 'true') return;

  const { Capacitor } = await import('@capacitor/core');
  if (Capacitor.isNativePlatform()) return;

  prepareServiceWorkerEnvironment()
    .catch((error) => {
      console.error('Service worker cleanup failed:', error);
    })
    .finally(() => {
      const updateSW = registerSW({
        onNeedRefresh() {
          window.__pwaUpdateDetectedAt = Date.now();
          window.__pwaUpdateAvailable = true;
          window.__pwaDoUpdate = () =>
            updateSW(true).catch(async (error) => {
              console.error('Service worker update failed:', error);
              if (pwaRecoveryAttempted) {
                return;
              }

              pwaRecoveryAttempted = true;
              await clearAppBrowsingData().catch((cleanupError) => {
                console.error('PWA recovery cleanup failed:', cleanupError);
              });

              window.__pwaUpdateAvailable = false;
              window.__pwaUpdateDetectedAt = 0;
              window.location.reload();
            });
          window.dispatchEvent(new Event('pwa-update-available'));

          if (document.visibilityState === 'hidden') {
            updateSW(true);
          }
        },
        onRegisteredSW(_swUrl, r) {
          if (!r) return;
          if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
              if (hasReloadedForServiceWorker) return;
              hasReloadedForServiceWorker = true;
              window.location.reload();
            });
          }

          if (r.waiting) {
            updateSW(true);
          }

          r.addEventListener('updatefound', () => {
            const worker = r.installing;
            if (!worker) return;

            worker.addEventListener('statechange', () => {
              if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                updateSW(true);
              }
            });
          });

          if (isAppControlledByUnexpectedWorker()) {
            r.update().catch(() => {});
          }

          r.update().catch(() => {});
          setInterval(() => r.update().catch(() => {}), 60 * 1000);
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') r.update().catch(() => {});
          });
        },
      });

      window.__pwaDoUpdate = () =>
        updateSW(true).catch(async (error) => {
          console.error('Service worker update failed:', error);
          if (pwaRecoveryAttempted) {
            return;
          }

          pwaRecoveryAttempted = true;
          await clearAppBrowsingData().catch((cleanupError) => {
            console.error('PWA recovery cleanup failed:', cleanupError);
          });

          window.__pwaUpdateAvailable = false;
          window.__pwaUpdateDetectedAt = 0;
          window.location.reload();
        });
    });
}

void bootServiceWorkerRegistration();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ShopProvider>
          <App />
        </ShopProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);
